import { debounce } from 'lodash';
import {
    useOrganizationStore,
    useSegmentStore,
    useSubscriptionsStore,
    useUserStore,
} from 'store/modules';

const isAssuming = Boolean(window.sessionStorage.getItem('assume_token'));

const handleUserData = () => {
    if (isAssuming) return;

    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();
    const subscriptionsStore = useSubscriptionsStore();
    const segmentStore = useSegmentStore();

    const organization = organizationStore.organization;
    const subscription = subscriptionsStore.subscription;
    const user = userStore.user;
    const config = userStore.config;

    if (!user || !config) return;
    if (window.Alvis) {
        const attr = {};

        if (subscription) {
            attr.company_id = organization.id;
        }

        window.Alvis('identify', user.id, attr);
    }

    if (window.Config.assuming) return;

    const userId = user.id;
    const userData = {
        user_avatar: user.photoUrl,
        organization_id: organization.id,
        organization_name: organization.name,
        user_email: user.email,
        user_firstName: user.firstName,
        user_lastName: user.lastName,
        user_name: user.fullName,
        user_role: user.role,
        user_createdAt: window.moment.utc(user.createdAt).format('YYYY-MM-DD'),
        identify_type: 'manual',
    };

    const intercomContext = {
        Intercom: {
            user_hash: config.intercom,
        },
    };

    segmentStore.updateSegmentEvents({
        eventType: 'identify',
        payload: {
            userId,
            traits: userData,
            context: intercomContext,
        },
    });

    window.analytics?.identify(userId, { id: userId, ...userData }, intercomContext);
};

const handleOrganizationData = () => {
    if (isAssuming) return;

    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();
    const subscriptionsStore = useSubscriptionsStore();
    const segmentStore = useSegmentStore();

    const organization = organizationStore.organization;
    const subscription = subscriptionsStore.subscription;
    const user = userStore.user;

    const organizationId = organization.id;
    const userId = user.id;

    const organizationData = {
        organization_createdAt: window.moment.utc(organization.created_at).unix(),
        organization_name: organization.name,
        people_owner_firstName: organization.owner.firstName,
        people_owner_id: organization.owner.id,
        people_owner_lastName: organization.owner.lastName,
        people_owner_name: organization.owner.fullName,
        people_owner_phone: organization.owner.number,
        billing_subscription_planName: subscription ? subscription.plan.id : 'unknown',
        billing_subscription_status: subscription ? subscription.status : 'unknown',
        group_type: 'manual',
    };

    segmentStore.updateSegmentEvents({
        eventType: 'group',
        payload: {
            userId,
            groupId: organizationId,
            traits: organizationData,
        },
    });

    window.analytics?.group(organizationId, organizationData);
};

const handleSegmentEvent = (event, payload, options, otherOptions = null) => {
    if (isAssuming) return;

    try {
        payload = payload || {};
        options = options || {};

        const userStore = useUserStore();
        const organizationStore = useOrganizationStore();
        const segmentStore = useSegmentStore();

        const user = otherOptions?.user || userStore.user;
        const organization = organizationStore.organization;

        if (organization) options.groupId = organization.id;

        const properties = Object.assign(payload, {
            context: options,
        });

        segmentStore.updateSegmentEvents({
            eventType: 'track',
            payload: {
                userId: user ? user.id : 'anonymous_user',
                event: event,
                properties: properties,
                context: options,
            },
        });
    } catch (e) {
        console.error(e);
    }
};

export const setUserData = debounce(handleUserData, 500);
export const setOrganizationData = debounce(handleOrganizationData, 500);
export const segmentEvent = debounce(handleSegmentEvent, 500);
